<template>
  <v-card
    class="pa-2 elevation-0 d-flex flex-column"
    height="100%"
  >
    <v-card-title class="subtitle-1 font-weight-bold darkblue--text">
      <v-col>
        <v-row>
          <v-col
            class="pb-0 col-grow text-truncate"
            :title="project.projectName"
          >
            <span
              class="cursor-pointer"
              @click="goToProject(project)"
            >
              {{ project.projectName }}
            </span>
          </v-col>

          <v-col class="col-auto pb-0 px-0 midgray--text text-right">
            <!-- Status -->
            <v-chip
              :class="`ml-2 ${project.projectStatusColorText}--text`"
              :color="project.projectStatusColorBackground"
              small
            >
              {{ projectStatus }}
            </v-chip>
          </v-col>

          <v-col class="col-auto pb-0 pt-2 px-0 midgray--text">
            <!-- Actions -->
            <v-menu
              offset-y
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="['fas', 'ellipsis-vertical']"
                    size="lg"
                  />
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                  v-for="action in actions"
                  :key="action.name"
                  @click="action.event(project)"
                >
                  <v-list-item-title>{{ action.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="pt-0">
            <v-row class="body-2 mt-0">
              <v-col :class="`col-auto py-0 font-weight-medium ${textColor}--text`">
                {{ project.billingCode || 'Billing Code TBD' }}
              </v-col>

              <v-col class="col-auto pa-0">
                <font-awesome-icon
                  :icon="['fas', 'circle']"
                  size="xs"
                  class="px-1 midgray--text"
                  style="font-size: 6px; margin-bottom: 1.5px;"
                />
              </v-col>

              <v-col :class="`col-auto py-0 font-weight-medium ${textColor}--text`">
                {{ project.projectCode || 'Project Code TBD' }}
              </v-col>
            </v-row>

            <v-row>
              <v-col :class="`col-auto py-0 font-weight-medium ${textColor}--text`">
                {{ projectType || 'None' }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-card-title>

    <v-card-text>
      <v-col>
        <v-row v-if="showProjectDescription">
          <v-col>
            <v-row class="subtitle-2">
              <v-col class="midgray--text">
                Description
              </v-col>
            </v-row>

            <v-row class="body-2 mt-0">
              <v-col :class="`pt-0 font-weight-medium ${textColor}--text`">
                {{ project.projectDescription }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Progress Bar -->
        <v-row v-if="project.isLoadingTimeEntries">
          <v-col class="pt-4">
            <v-row>
              <v-col class="pb-0 midgray--text">
                Loading time entries...
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-skeleton-loader
                  type="heading"
                  :loading="project.isLoadingTimeEntries"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col>
            <v-row class="subtitle-2">
              <v-col class="pb-0">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.totalBillableTimeFormatted }}
                    </span>
                  </template>
                  <span>
                    Total billable hours logged for this project
                  </span>
                </v-tooltip>
                <span class="px-1 midgray--text">/</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.projectHoursFormatted }}
                    </span>
                  </template>
                  <span>
                    Total allocated hours for this project
                  </span>
                </v-tooltip>
                <span class="pl-1 midgray--text">Hrs</span>
              </v-col>

              <v-col class="pb-0 midgray--text text-right">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`pr-1 ${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.projectMinutesConsumedPercentFormatted }}%
                    </span>
                  </template>
                  <span>
                    Percentage of project hours consumed
                  </span>
                </v-tooltip>
                <span class="midgray--text">Hrs</span>
              </v-col>
            </v-row>

            <v-row
              class="body-2 mt-2 cursor-pointer"
              @click="showTimeEntries(project)"
            >
              <v-col :class="`font-weight-medium ${textColor}--text`">
                <div
                  class="icewhite"
                  style="width: 100%;"
                >
                  <div
                    :class="project.projectStatusColorBackground"
                    :style="`height: 8px; width: ${ Math.min(project.projectMinutesConsumedPercentage, 100) }%;`"
                  />
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-0 subtitle-2">
              <v-col class="col-auto pt-2">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.startDateFormatted || '--' }}
                    </span>
                  </template>
                  <span>
                    Project start date
                  </span>
                </v-tooltip>

                <span class="px-1 midgray--text">to</span>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.endDateFormatted || '--' }}
                    </span>
                  </template>
                  <span>
                    Project end date
                  </span>
                </v-tooltip>
              </v-col>

              <v-col class="text-right pt-2">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      :class="`pr-1 ${textColor}--text`"
                      v-on="on"
                    >
                      {{ project.projectDaysRemaining }}
                    </span>
                  </template>
                  <span>
                    Number of days remaining until project end date
                  </span>
                </v-tooltip>
                <span class="midgray--text">{{ project.projectDaysRemaining === 1 ? 'Day Remains' : 'Days Remain' }}</span>
              </v-col>
            </v-row>

            <v-row class="mt-0">
              <v-col>
                <v-divider />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-card-text>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'ProjectCard',
  props: {
    actions: {
      type: Array,
      required: true,
    },
    project: {
      type: Object,
      required: true,
    },
    projectStatus: {
      type: String,
      required: true,
    },
    projectType: {
      type: String,
      required: false,
      default: 'None',
    },
  },
  data() {
    return {
      showProjectDescription: false,
    }
  },
  computed: {
    ...mapAuthGetters(['inDarkMode']),
    textColor() {
      return this.inDarkMode ? 'gray' : 'nearblack'
    },
    // Computed property to ensure reactivity when project updates
    reactiveProject() {
      return { ...this.project }
    },
  },
  watch: {
    project: {
      deep: true,
      immediate: true,
      handler(newProject, oldProject) {
        // Force re-render when project data changes (especially loading states)
        if (newProject && oldProject) {
          const loadingChanged = newProject.isLoadingTimeEntries !== oldProject.isLoadingTimeEntries
          const timeDataChanged = newProject.totalBillableTimeFormatted !== oldProject.totalBillableTimeFormatted

          if (loadingChanged || timeDataChanged) {
            this.$forceUpdate()
          }
        }
      },
    },
  },
  methods: {
    goToProject(project) {
      this.$emit('go-to-project', project)
    },
    showTimeEntries(project) {
      this.$emit('show-time-entries', project)
    },
  },
}
</script>
